import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { TOKEN } from "shared/constants/LocalStorageConsts";
import { logout } from "shared/helpers/auth-functions";
import snakeToCamelCase from "shared/helpers/snakeToCamelCase";
import { Cupom } from "shared/interfaces/Cupom";
import { Member } from "shared/interfaces/Member";
import { PaymentTransaction } from "shared/interfaces/PaymentTransaction";
import { RequestArtistInformationAdjustment } from "shared/interfaces/RequestArtistInformationAdjustment";
import { Team } from "shared/interfaces/Team";
import { UserFilter } from "shared/interfaces/UserFilter";
import { Usuario } from "shared/interfaces/Usuario";
import i18n from "i18next";
const pendingRequests = new Map();

export const instance = axios.create({
  baseURL: process.env.REACT_APP_HOST_ENDPOINT,
});

const apisToPreventDuplicates = ["user/register-user"];

const isUrlInList = (url: string, list: Array<string | RegExp>): boolean => {
  return list.some((pattern) => {
    if (pattern instanceof RegExp) {
      return pattern.test(url);
    }
    return url.includes(pattern);
  });
};

instance.interceptors.request.use((config: AxiosRequestConfig) => {
  config.headers["Authorization"] = `Bearer ${localStorage.getItem(TOKEN)}`;
  config.headers["Language"] = i18n.language;
  return config;
});

instance.interceptors.request.use((config: AxiosRequestConfig) => {
  const key = `${config.method}:${config.url}`;

  if (
    isUrlInList(config.url || "", apisToPreventDuplicates) &&
    pendingRequests.has(key)
  ) {
    throw new axios.Cancel("Operação duplicada");
  }
  if (isUrlInList(config.url || "", apisToPreventDuplicates)) {
    pendingRequests.set(key, true);
  }

  return config;
});

instance.interceptors.response.use(
  (response: AxiosResponse<any>) => {
    if (isUrlInList(response.config.url || "", apisToPreventDuplicates)) {
      const key = `${response.config.method}:${response.config.url}`;
      pendingRequests.delete(key);
    }

    return response.data;
  },
  (error: AxiosError) => {
    if (axios.isCancel(error)) {
      console.log("Requisição cancelada:", error.message);
    } else if (error.response) {
      switch (error.response.status) {
        case 401:
        case 403:
          logout();
          break;
      }
    }
    return Promise.reject(error);
  }
);

export const fillFileRequestProps = (context: any) => {
  return {
    action: `${process.env.REACT_APP_HOST_ENDPOINT}file/upload?contextName=${context.contextName}&key=${context.key}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem(TOKEN)}`,
    },
  };
};

const camelCaseTransform = () => {
  const cloneInstance = axios.create({
    baseURL: process.env.REACT_APP_HOST_ENDPOINT,
    timeout: 180000,
  });
  cloneInstance.interceptors.request.use((config: AxiosRequestConfig) => {
    config.headers["Authorization"] = `Bearer ${localStorage.getItem(TOKEN)}`;
    config.headers["Language"] = i18n.language;
    return config;
  });
  cloneInstance.interceptors.response.use(
    (response: AxiosResponse<any>) => snakeToCamelCase(response.data, true),
    (error: any) => {
      switch (error.response.status) {
        case 401:
        case 403:
          logout();
      }
      return error;
    }
  );
  return cloneInstance;
};

export const getLoggedUserDistroToken = () =>
  instance.get("auth/get-logged-user-distro-token");

export const fetchUserDeviceCheck = (browserId: string) =>
  instance.get(`auth/user-device-check/${browserId}`);

export const validateUserDeviceCode = (code: string, browserId: string) =>
  instance.post(`auth/user-device-validate-code/${browserId}/?code=${code}`);

export const fetchLoggedUser = (browserId: string) =>
  instance.get(`user/user-logged-info/${browserId}`);

export const fetchPersonifiedUser = () =>
  instance.get(`user/user-personified-info`);

export const fetchUserByEmail = (email: string) =>
  instance.get(`user/get-by-email/${email}`);

export const fetchUserDiagnostics = () =>
  instance.get("diagnostic/user-logged-list");

export const fetchUserCredits = () =>
  instance.get("diagnostic/user-logged-credits");

export const fetchUsers = () => instance.get("user/get-users");

export const fetchUsersFilter = (data: any) =>
  instance.post("user/get-list", data);

export const putAddUserDistroByEmail = (params: any) =>
  instance.put(`user/add-distro`, params);

export const exportUsersCSV = (filter: UserFilter) =>
  instance.post(`user/get-list-csv`, {
    params: filter,
  });

export const exportUsersExcel = (filter: UserFilter) =>
  instance.post(`user/get-list-excel`, filter, {
    responseType: "arraybuffer",
  });

export const exportUsersChartsExcel = () =>
  instance.get(`user/export-users-charts-excel`, {
    responseType: "arraybuffer",
  });

export const exportPaymentTransactionsCSV = (filter: any) =>
  instance.post("paymenttransaction/export-csv", filter, {
    responseType: "arraybuffer",
  });

export const exportPaymentTransactionsExcel = (filter: any) =>
  instance.post("paymenttransaction/export-excel", filter, {
    responseType: "arraybuffer",
  });

export const createUser = (usuario: Usuario) =>
  instance.post("user/create-user", usuario);

export const registerUser = (usuario: Usuario) =>
  instance.post("user/register-user", usuario);

export const editUser = (user: Usuario) => instance.put("user/edit-user", user);

export const fetchUserById = (id: number) =>
  instance.get(`user/get-user-by/${id}`);

export const getArtistDashboard = (artistId: number) =>
  instance.get(`/dashboard/artist-info/${artistId}`);

export const fetchIndividualMetrics = (artistId: string) =>
  instance.get(`dashboard/artist-info-individual-metrics/${artistId}`);

export const saveLoggedUser = (user: Usuario) =>
  instance.put("user/user-logged-update-info", user);

export const putChangeLanguage = (lang: string) =>
  instance.put(`user/user-logged-update-language?userCurrentLanguage=${lang}`);

export const fetchUrlCheckoutByQuantity = (params: any) =>
  instance.put(`diagnostic/generate-team-buy-diagnostic-endpoint/`, params);

export const validateDiagnosePurchase = (paymentId: string) =>
  instance.put(`diagnostic/validate-user-diagnostic-purchase/${paymentId}`);

export const fetchCountries: any = () => instance.get("quiz/country-list");

export const generateUserDiagnoseToken = () =>
  instance.get("diagnostic/generate-user-diagnostic-token");

export const fetchGenres: any = () => instance.get("quiz/genre-list");

export const fetchArtistsRelation: any = () =>
  instance.get("quiz/artist-relationship-list");

export const fetchSpotifyArtists = (term: string) =>
  instance.get(`quiz/spotify-artist-list/${term}`);

export const fetchYoutubeArtists = (term: string) =>
  instance.get(`quiz/youtube-artist-list/${term}`);

export const fetchInstagramUser = (term: string) =>
  instance.get(`quiz/validate-instagram-username/${term}`);

export const fetchInstagramList = (term: string) =>
  instance.get(`quiz/instagram-artist-list/${term}`);

export const saveQuiz: any = (quiz: any) => instance.post("quiz/save", quiz);

export const saveQuizToUser: any = (quiz: any) =>
  instance.post("quiz/save-assigning-user", quiz);

export const fetchArtistQuiz: any = (id: any) =>
  instance.get(`quiz/get-quiz-artist-by-id/${id}`);

export const validateCoupon = (coupon: string) =>
  instance.put(`coupon/validate-cupom/${coupon}`);

export const fetchCoupons: any = () => instance.get("coupon/get-coupon-list");

export const createCoupon = (coupon: Cupom) =>
  instance.put("coupon/create-new-coupon", coupon);

export const disableCoupon = (params: Cupom) =>
  instance.delete("coupon/disable-coupon", { data: params });

export const fetchPainelList = () => instance.get("panel/get-panel-list");

export const getInstagramUserName = (code: string) =>
  instance.get(`auth/get-inst-username/${code}`);

export const fetchLoggedUserTeams = () => instance.get("user/user-logged-team");

export const fetchTeams = () => instance.get("team/get-list");

export const fetchPaymentTransactions = (filter: any) =>
  instance.post("PaymentTransaction/get-payment-transactions", filter);

export const deleteTeam = (id: string) =>
  instance.delete(`team/remove-team/${id}`);

export const AddUserTeam = (params: Member) =>
  instance.post("team/add-user-team", params);

export const updateTeamInfo = (params: Team) =>
  instance.put("team/update-team-info", params);

export const setTeamName = (params: any) =>
  instance.put("team/set-team-name", params);

export const setTeamLogins = (params: any) =>
  instance.put("team/set-team-logins", params);

export const deleteTeamMember = (data: any) =>
  instance.delete("team/remove-user-from-team", { data });

export const getTeamById = (id: number) =>
  instance.get(`team/get-team-by-id/${id}`);

export const createUserCompany = (data: any) =>
  instance.post("user/create-team-user-company", data);

export const createTeamUserPerson = (data: any) =>
  instance.post("user/create-team-user-person", data);

export const updateTeamMembers = (params: any) =>
  instance.post("team/update-logins", params);

export const approveCommercialReview = (params: any) =>
  instance.put("team/approve-commercial-review", params);

export const cancelAutoRenewal = (params: any) =>
  instance.put("team/cancel-auto-renew", params);

export const putCancelAutoRenewal = (id: string) =>
  instance.put(`team/cancel-auto-renew/${id}`);

export const putCreateTeam = () => instance.post("team/create-new-team", {});

export const createInviteDiagnostic = (params: any) =>
  instance.put("teamuserdiag/create-diag-invite-request", params);

export const shareDiagnosticByEmail = (params: any) =>
  instance.put("teamuserdiag/share-by-email", params);

export const remiderDiagnosticByEmail = (diagnosisId: string) =>
  instance.put(`teamuserdiag/remind-by-email/${diagnosisId}`);

export const deleteDiagnostic = (diagnosisId: string) =>
  instance.delete(`teamuserdiag/remove-diag-invite-request/${diagnosisId}`);

export const getDiagnosticPrice = () =>
  instance.get("diagnostic/get-diag-team-unit-price");

export const savePaymentTransaction = (params: PaymentTransaction) =>
  instance.put("paymentTransaction/save", params);

export const deletePaymentTransaction = (id: string) =>
  instance.delete(`paymentTransaction/remove/${id}`);

export const getDiagnosticById = (id: string) =>
  camelCaseTransform().get(`Diagnostic/get-diag-by-id/${id}`);

export const requestArtistInformationAdjustment: any = (
  body: RequestArtistInformationAdjustment
) => instance.post(`Artist/request-artist-information-adjustment`, body);

export const subscribeNewsletter = () =>
  instance.post("user/subscribe-newsletter");

export const fetchRecentlyAccessedArtists = () =>
  instance.get("user/user-logged-recently-accessed-artists-rank");

export const fetchFollowedArtists = () =>
  instance.get("user/user-logged-following-artists-list-rank");

export const addArtistsToFollow = (artistId: string) =>
  instance.post(`user/add-artist-to-following-list/${artistId}`);

export const removeArtistsFromFollow = (artistId: string) =>
  instance.delete(`user/remove-artist-to-following-list/${artistId}`);

export const fetchAdvances = (params: any) =>
  instance.post("cashadvance/get-by-filter", params);

export const deleteCashAdvance = (advanceId: any) =>
  instance.delete(`cashadvance/remove/${advanceId}`);

export const fetchAdvanceById = (advanceId: string) =>
  instance.get(`cashadvance/get-by-id/${advanceId}`);

export const createAdvance = (userId: string) =>
  instance.put(`cashadvance/create-new-via-backoffice/${userId}`);

export const fetchAdvanceReleases = (advanceId: string) =>
  instance.get(`cashadvance/get-advance-releases/${advanceId}`);

export const putAssignCashAdvance = (params: any) =>
  instance.put("cashadvance/assign-releases", params);

export const updateAdvance = (params: any) =>
  instance.put("cashadvance/save", params);

export const fetchInbox = () => instance.get("cashadvance/get-inbox-list");

export const takeOverCashAdvanceProcess = (id: string) =>
  instance.put(`cashadvance/assign-to-logged-user/${id}`);

export const giveBackCashAdvanceProcess = (id: string) =>
  instance.put(`cashadvance/assign-to-pool/${id}`);

export const putValidateDocumentCashAdvance = (params: any) =>
  instance.put("cashadvance/validate-distro-documents", params);

export const changeStatusCashAdvanceProcess = (id: string, status: any) =>
  instance.put(`cashadvance/change-status/${id}/${status}`);

export const downloadAdvanceSignedContract = (cashAdvanceId: string) =>
  instance.get(`cashadvance/generate-contract/${cashAdvanceId}`, {
    responseType: "arraybuffer",
  });

export const exportAdvanceCSV = (params: any) =>
  instance.post("cashadvance/export-csv", params);

export const rejectCashAdvanceReports = (advanceId: any) =>
  instance.put(`cashadvance/reject-documents/${advanceId}`);

export const changeAmountCashAdvanceReports = (advanceId: any, amount: any) =>
  instance.put(`cashadvance/change-amount/${advanceId}/${amount}`);

export const putGenerateConsolidation = (params: any) =>
  instance.put(`cashadvance/generate-consolidation`, params);

export const putGenerateQuotes = (cashAdvanceId: any) =>
  instance.put(`cashadvance/generate-quotes/${cashAdvanceId}`);

export const exportAdvanceExcel = (filter: any) =>
  instance.post("cashadvance/export-excel", filter, {
    responseType: "arraybuffer",
  });

export const putCalculateCashAdvanceRelease = (
  params: any,
  userArtistId: string
) =>
  instance.put(
    `cashadvance/calculate-advance-total-amount/${userArtistId}`,
    params
  );

export const getAddressByCep = (cep: string) =>
  instance.get(`cep/get-address-by-code/${cep}`);

export const personifyUser = (userId: string) =>
  instance.put(`auth/personify-user/${userId}`);

export const depersonifyUser = () =>
  instance.delete(`auth/depersonify-logged-user`);

export const postCreateMigration = (data: any) =>
  instance.post(`catalogmigration/create-migration`, data);

export const postSaveMigration = (data: any) =>
  instance.post(`catalogmigration/save`, data);

export const deleteCatalogMigration = (idMigration: any) =>
  instance.delete(`catalogmigration/remove-migration/${idMigration}`);

export const fetchCatalogMigrations = (data: any) =>
  instance.post(`catalogmigration/get-list`, data);

export const fetchCatalogMigrationById = (catalogMigrationId: string) =>
  instance.get(`catalogmigration/get-by-id/${catalogMigrationId}`);

export const fetchCatalogMigrationReleaseById = (releaseId: string) =>
  instance.get(`catalogmigration/get-release-detail-by-id/${releaseId}`);

export const putAssignCatalogMigrationToLoggedUser = (
  catalogMigrationId: string
) =>
  instance.put(`catalogmigration/assign-to-logged-user/${catalogMigrationId}`);

export const putAssignCatalogMigrationToPool = () =>
  instance.put(`catalogmigration/assign-to-pool/{catalogMigrationId}`);

export const putAppendReleaseToMigration = (params: any) =>
  instance.put(`catalogmigration/append-upcs-migration`, params);

export const createMigrationReleases = (catalogMigrationId: string) =>
  instance.put(`catalogmigration/create-releases/${catalogMigrationId}`);

export const publishMigrationReleases = (catalogMigrationId: string) =>
  instance.put(`catalogmigration/publish-releases/${catalogMigrationId}`);

export const resetMetadadosCatalogMigrationRelease = (
  catalogMigrationReleaseId: string
) =>
  instance.put(`catalogmigration/reset-metadata/${catalogMigrationReleaseId}`);

export const deleteCatalogMigrationRelease = (
  catalogMigrationReleaseId: string
) =>
  instance.delete(
    `catalogmigration/remove-release/${catalogMigrationReleaseId}`
  );

export const validateReleases = (catalogMigrationId: string) =>
  instance.put(`catalogmigration/validate-releases/${catalogMigrationId}`);

export const validateRelease = (catalogMigrationReleaseId: string) =>
  instance.put(
    `catalogmigration/validate-release/${catalogMigrationReleaseId}`
  );

export const exportCatalogMigrationToCsv = (data: any) =>
  instance.post(`catalogmigration/export-csv`, data);

export const exportCatalogMigrationToExcel = (data: any) =>
  instance.post(`catalogmigration/export-excel`, data, {
    responseType: "arraybuffer",
  });

export const exportCatalogMigrationMetadata = (catalogmigrainId: any) =>
  instance.get(
    `catalogmigration/export-release-validations/${catalogmigrainId}`,
    {
      responseType: "arraybuffer",
    }
  );

export const fecthBankList = () => instance.get(`utils/bank-list`);

export const getAdvanceSignedContract = (cashAdvanceId: string) =>
  instance.put(`cashadvance/generate-contract-link/${cashAdvanceId}`);

export const getAdvanceQuotes = (cashAdvanceId: string) =>
  instance.get(`cashadvance/quotes/get-list/${cashAdvanceId}`);

export const saveAdvanceQuote = (params: any) =>
  instance.post(`cashadvance/quote/upsert`, params);

export const deleteAdvanceQuote = (cashAdvanceQuoteId: string) =>
  instance.delete(`cashadvance/quote/${cashAdvanceQuoteId}`);

export const getUrlUpload = (params: any) =>
  instance.put(`file/generate-upload-url`, params);

export const getFeatureConfigByKey = (key: string) =>
  instance.get(`Configurations/get-config-by-key/${key}`);

export const updateFeatureConfig = (params: any) =>
  instance.put(`Configurations/update-config-value`, params);

export const getSpecialCampaignRequests = (params: any) =>
  instance.post(`SpecialCampaignRequest/get-by-filter`, params);

export const updateApprovalSpecialCampaignRequest = (id: string, params: any) =>
  instance.put(`SpecialCampaignRequest/${id}`, params);

export const getDistroTokenByLoggedUserTeam = () =>
  instance.get(`team/get-revelator-token-by-logged-user`);
